import { Alert, Box, Button, Stack, TextField } from '@mui/material';
import { FormEvent, useState } from 'react';

interface Props {
  onSubmit: (email: string, password: string) => Promise<void>;
  onForgotPassword: () => void;
  loading: boolean;
  error: string;
  onErrorClear: () => void;
}

export const LoginForm = ({
  onSubmit,
  onForgotPassword,
  loading,
  error,
  onErrorClear,
}: Props) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  const validateForm = (): boolean => {
    const errors: { email?: string; password?: string } = {};

    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Please enter a valid email';
    }

    if (!password) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      await onSubmit(email, password);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={3} sx={{ mt: 1 }}>
        <TextField
          label="Email Address"
          type="email"
          required
          fullWidth
          autoComplete="email"
          data-qaid="email-input"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
        />

        <TextField
          label="Password"
          type="password"
          required
          fullWidth
          autoComplete="current-password"
          data-qaid="password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          error={!!validationErrors.password}
          helperText={validationErrors.password}
        />

        {error && (
          <Alert severity="error" onClose={onErrorClear}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          size="large"
          data-qaid="login-button"
        >
          {loading ? 'Logging in...' : 'Login'}
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={onForgotPassword}
            variant="text"
            size="small"
            data-qaid="forgot-password-button"
          >
            Forgot Password?
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
